import { z } from 'zod';
import { CARD_CAPTURE_PAYMENT_METHOD, CARD_PROVIDERS } from './card-capture.js';
import { type Currency, isCurrency, minorUnitFactor } from './currency.js';
import type { DonationPaymentMethod } from './donation.js';
import { fromDecimalString, type Money } from './money.js';
import { sha256Hex } from './sha256.js';
import { lowerTurkish } from './text.js';

export const STATEMENT_FORMATS = ['xlsx_mapped', 'csv_mapped', 'camt053', 'mt940'] as const;
export type StatementFormat = (typeof STATEMENT_FORMATS)[number];

/**
 * Where a line in `account_transactions` came from: a bank statement, one of
 * the payment providers, or a payment somebody typed — cash into a cash
 * account, or a transfer or cheque from a slip into a bank account
 * (docs/decisions/0100). Derived from `CARD_PROVIDERS` so a provider the
 * database admits cannot be missing from the contract, which is what two
 * hand-written copies of this list did to `paypal`.
 */
export const ACCOUNT_TRANSACTION_SOURCES = [
  'bank_statement',
  ...CARD_PROVIDERS,
  'manual_cash',
  'manual_bank',
] as const;
export type AccountTransactionSource = (typeof ACCOUNT_TRANSACTION_SOURCES)[number];

/**
 * How the money on a line of each source arrived, which is what a donation
 * recorded `unspecified` learns when such a line is matched to it: a bank
 * statement line is a transfer, a provider's capture is that provider's
 * method, a cash entry is cash —
 * docs/decisions/0070-the-server-decides-four-fields-and-a-donation-names-its-parties.md
 * *Amended 2026-09-23*.
 */
export const PAYMENT_METHOD_PROVEN_BY_SOURCE: Readonly<
  Record<AccountTransactionSource, DonationPaymentMethod>
> = {
  bank_statement: 'bank_transfer',
  ...CARD_CAPTURE_PAYMENT_METHOD,
  manual_cash: 'cash',
  manual_bank: 'bank_transfer',
};

export const ACCOUNT_TRANSACTION_DIRECTIONS = ['credit', 'debit'] as const;
export type AccountTransactionDirection = (typeof ACCOUNT_TRANSACTION_DIRECTIONS)[number];

export const ACCOUNT_TRANSACTION_CLASSIFICATIONS = [
  'donor_payment',
  'card_settlement',
  'fee',
  'refund_payout',
  'internal_transfer',
  'other',
] as const;
export type AccountTransactionClassification = (typeof ACCOUNT_TRANSACTION_CLASSIFICATIONS)[number];

const columnSchema = z
  .string()
  .regex(/^[A-Z]{1,2}$/, 'A column is a spreadsheet letter such as A or AB');

const datedColumnSchema = z.object({
  column: columnSchema,
  format: z.string().min(4).max(40),
});

/**
 * A rule that names what a row **is**, so nothing is dropped for being
 * uninteresting: the legacy import discarded every Is Bankasi row whose column
 * H read `POS`, which is exactly the card payout line §5.7 reconciles.
 */
export const statementClassificationRuleSchema = z
  .object({
    column: columnSchema,
    equals: z.string().min(1).optional(),
    contains: z.string().min(1).optional(),
    classify: z.enum(ACCOUNT_TRANSACTION_CLASSIFICATIONS),
  })
  .refine(
    (rule) => (rule.equals === undefined) !== (rule.contains === undefined),
    'A classification rule matches on equals or on contains, not both and not neither',
  );

export const statementAmountMappingSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('signed'), column: columnSchema }),
  z.object({
    kind: z.literal('split'),
    debitColumn: columnSchema,
    creditColumn: columnSchema,
  }),
]);

/**
 * How one bank's export is read, for the `xlsx_mapped` and `csv_mapped`
 * formats. A profile version is immutable: an edit inserts the next version and
 * an import pins the version it was parsed with, so re-reading a file always
 * produces the lines that were committed.
 */
export const statementMappingSchema = z
  .object({
    headerRow: z.number().int().min(1),
    firstDataRow: z.number().int().min(2),
    date: datedColumnSchema,
    valueDate: datedColumnSchema.optional(),
    amount: statementAmountMappingSchema,
    decimalSeparator: z.enum([',', '.']),
    thousandsSeparator: z.enum([',', '.', ' ', "'", '']),
    description: columnSchema.optional(),
    reference: columnSchema.optional(),
    counterpartyName: columnSchema.optional(),
    counterpartyIban: columnSchema.optional(),
    balanceAfter: columnSchema.optional(),
    currencyColumn: columnSchema.optional(),
    /** Detected from the header line when absent; only a CSV profile reads it. */
    csvDelimiter: z.enum([';', ',', '\t', '|']).optional(),
    /**
     * What the header row must say, column by column, compared without case or
     * surrounding space. A file that does not say it was exported by another
     * bank or another screen, and is refused whole rather than read into the
     * wrong columns.
     */
    expectedHeaders: z.record(columnSchema, z.string().trim().min(1).max(200)).optional(),
    classifications: z.array(statementClassificationRuleSchema).max(50).default([]),
  })
  .refine(
    (mapping) => mapping.decimalSeparator !== mapping.thousandsSeparator,
    'The decimal and thousands separators must differ, or 1.234 is ambiguous',
  )
  .refine(
    (mapping) => mapping.firstDataRow > mapping.headerRow,
    'Data starts below the header row',
  );

export type StatementMapping = z.infer<typeof statementMappingSchema>;

export const STATEMENT_ROW_ISSUES = [
  'ROW_EMPTY',
  'DATE_MISSING',
  'DATE_UNPARSEABLE',
  'AMOUNT_MISSING',
  'AMOUNT_UNPARSEABLE',
  'AMOUNT_ZERO',
  'AMOUNT_AMBIGUOUS',
  'CURRENCY_UNKNOWN',
  'BALANCE_UNPARSEABLE',
  'ENTRY_NOT_BOOKED',
  'BALANCE_GAP',
  'DUPLICATE_IN_FILE',
  'ALREADY_IMPORTED',
  'DUPLICATE_ON_COMMIT',
  'CURRENCY_NOT_ACCOUNT',
] as const;
export type StatementRowIssue = (typeof STATEMENT_ROW_ISSUES)[number];

export type ParsedStatementLine = {
  readonly bookedOn: string;
  readonly valueDate: string | null;
  readonly direction: AccountTransactionDirection;
  readonly amount: Money;
  readonly description: string | null;
  readonly externalRef: string | null;
  readonly counterpartyName: string | null;
  readonly counterpartyIban: string | null;
  readonly balanceAfterMinor: bigint | null;
  readonly classification: AccountTransactionClassification;
};

export type ParsedStatementRow = {
  readonly line: ParsedStatementLine | null;
  readonly issues: readonly StatementRowIssue[];
};

export type StatementRowCells = readonly (string | null | undefined)[];

export function columnIndex(column: string): number {
  let index = 0;
  for (const letter of column) index = index * 26 + (letter.charCodeAt(0) - 64);
  return index - 1;
}

function cellOf(cells: StatementRowCells, column: string | undefined): string {
  if (column === undefined) return '';
  return (cells[columnIndex(column)] ?? '').toString().trim();
}

/**
 * One row of a mapped export, or the reasons it could not be read. A row that
 * parses with a soft issue keeps both: `BALANCE_UNPARSEABLE` is worth showing
 * in the preview and is no reason to refuse the money.
 *
 * `accountCurrency` is the account's own, used when the profile maps no
 * currency column. The currency is never a per-upload choice — the legacy asked
 * the uploader, and one wrong answer mispriced a whole statement.
 */
export function parseStatementRow(
  mapping: StatementMapping,
  cells: StatementRowCells,
  accountCurrency: Currency,
): ParsedStatementRow {
  const issues: StatementRowIssue[] = [];
  if (cells.every((cell) => (cell ?? '').toString().trim() === '')) {
    return { line: null, issues: ['ROW_EMPTY'] };
  }

  const bookedOn = parseDateCell(cellOf(cells, mapping.date.column), mapping.date.format);
  if (cellOf(cells, mapping.date.column) === '') issues.push('DATE_MISSING');
  else if (bookedOn === null) issues.push('DATE_UNPARSEABLE');

  const valueDateCell = mapping.valueDate ? cellOf(cells, mapping.valueDate.column) : '';
  const valueDate =
    mapping.valueDate && valueDateCell !== ''
      ? parseDateCell(valueDateCell, mapping.valueDate.format)
      : null;

  const currencyText = cellOf(cells, mapping.currencyColumn).toUpperCase();
  let currency: Currency = accountCurrency;
  if (mapping.currencyColumn !== undefined) {
    if (currencyText === '' || !isCurrency(currencyText)) issues.push('CURRENCY_UNKNOWN');
    else currency = currencyText;
  }

  const amount = readAmount(mapping, cells, currency, issues);

  let balanceAfterMinor: bigint | null = null;
  const balanceCell = cellOf(cells, mapping.balanceAfter);
  if (mapping.balanceAfter !== undefined && balanceCell !== '') {
    const balance = parseDecimalToMinor(balanceCell, currency, mapping);
    if (balance === null) issues.push('BALANCE_UNPARSEABLE');
    else balanceAfterMinor = balance;
  }

  if (bookedOn === null || amount === null) return { line: null, issues };

  const description = blankToNull(cellOf(cells, mapping.description));
  return {
    line: {
      bookedOn,
      valueDate,
      direction: amount.direction,
      amount: { amountMinor: amount.amountMinor, currency },
      description,
      externalRef: blankToNull(cellOf(cells, mapping.reference)),
      counterpartyName: blankToNull(cellOf(cells, mapping.counterpartyName)),
      counterpartyIban: blankToNull(cellOf(cells, mapping.counterpartyIban)),
      balanceAfterMinor,
      classification: classify(mapping, cells, amount.direction),
    },
    issues,
  };
}

type ReadAmount = { amountMinor: bigint; direction: AccountTransactionDirection };

function readAmount(
  mapping: StatementMapping,
  cells: StatementRowCells,
  currency: Currency,
  issues: StatementRowIssue[],
): ReadAmount | null {
  if (mapping.amount.kind === 'signed') {
    const text = cellOf(cells, mapping.amount.column);
    if (text === '') {
      issues.push('AMOUNT_MISSING');
      return null;
    }
    const minor = parseDecimalToMinor(text, currency, mapping);
    if (minor === null) {
      issues.push('AMOUNT_UNPARSEABLE');
      return null;
    }
    if (minor === 0n) {
      issues.push('AMOUNT_ZERO');
      return null;
    }
    return minor < 0n
      ? { amountMinor: -minor, direction: 'debit' }
      : { amountMinor: minor, direction: 'credit' };
  }

  const debitText = cellOf(cells, mapping.amount.debitColumn);
  const creditText = cellOf(cells, mapping.amount.creditColumn);
  const debit = debitText === '' ? 0n : parseDecimalToMinor(debitText, currency, mapping);
  const credit = creditText === '' ? 0n : parseDecimalToMinor(creditText, currency, mapping);
  if (debit === null || credit === null) {
    issues.push('AMOUNT_UNPARSEABLE');
    return null;
  }
  const debitMinor = absolute(debit);
  const creditMinor = absolute(credit);
  if (debitMinor === 0n && creditMinor === 0n) {
    issues.push(debitText === '' && creditText === '' ? 'AMOUNT_MISSING' : 'AMOUNT_ZERO');
    return null;
  }
  if (debitMinor !== 0n && creditMinor !== 0n) {
    issues.push('AMOUNT_AMBIGUOUS');
    return null;
  }
  return creditMinor !== 0n
    ? { amountMinor: creditMinor, direction: 'credit' }
    : { amountMinor: debitMinor, direction: 'debit' };
}

const absolute = (value: bigint): bigint => (value < 0n ? -value : value);

const blankToNull = (value: string): string | null => (value === '' ? null : value);

/**
 * An amount is read from its text by the profile's separator rules and never
 * through `Number`: the legacy's `round(str_replace(",","",$row["D"]),2)` read a
 * Turkish `1.234,56` as `1.23`, and silently lost three orders of magnitude.
 */
export function parseDecimalToMinor(
  text: string,
  currency: Currency,
  separators: Pick<StatementMapping, 'decimalSeparator' | 'thousandsSeparator'>,
): bigint | null {
  let cleaned = text.replace(/[\s  ]/g, '').replace(/^\+/, '');
  let negative = false;
  if (/^\(.*\)$/.test(cleaned)) {
    negative = true;
    cleaned = cleaned.slice(1, -1);
  }
  if (cleaned.endsWith('-')) {
    negative = true;
    cleaned = cleaned.slice(0, -1);
  }
  if (cleaned.startsWith('-')) {
    negative = true;
    cleaned = cleaned.slice(1);
  }
  if (separators.thousandsSeparator !== '') {
    cleaned = cleaned.split(separators.thousandsSeparator).join('');
  }
  const parts = cleaned.split(separators.decimalSeparator);
  if (parts.length > 2) return null;
  const [whole = '', fraction = ''] = parts;
  if (whole === '' || !/^\d+$/.test(whole)) return null;
  if (fraction !== '' && !/^\d+$/.test(fraction)) return null;

  const digits = minorUnitFactor(currency).toString().length - 1;
  if (fraction.length > digits) return null;
  const value = fromDecimalString(`${whole}.${fraction}`, currency).amountMinor;
  return negative ? -value : value;
}

const DATE_TOKENS: readonly [string, string][] = [
  ['YYYY', '(?<year>\\d{4})'],
  ['YY', '(?<shortYear>\\d{2})'],
  ['MM', '(?<month>\\d{2})'],
  ['DD', '(?<day>\\d{2})'],
];

/**
 * The date part of a cell, as `YYYY-MM-DD`. Anything after it is ignored, so
 * one `DD/MM/YYYY` format reads Is Bankasi's `date-time` column as well as
 * Ziraat's plain date.
 */
export function parseDateCell(text: string, format: string): string | null {
  if (text.trim() === '') return null;
  let pattern = format.replace(/[.*+?^${}()|[\]\\/-]/g, '\\$&');
  for (const [token, group] of DATE_TOKENS) pattern = pattern.replace(token, group);
  const match = new RegExp(`^${pattern}`).exec(text.trim());
  const groups = match?.groups;
  if (!groups) return null;

  const year = groups.year ?? (groups.shortYear ? `20${groups.shortYear}` : undefined);
  const { month, day } = groups;
  if (year === undefined || month === undefined || day === undefined) return null;
  const iso = `${year}-${month}-${day}`;
  return isRealDate(iso) ? iso : null;
}

function isRealDate(iso: string): boolean {
  const date = new Date(`${iso}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === iso;
}

function classify(
  mapping: StatementMapping,
  cells: StatementRowCells,
  direction: AccountTransactionDirection,
): AccountTransactionClassification {
  for (const rule of mapping.classifications) {
    const value = cellOf(cells, rule.column);
    if (rule.equals !== undefined && value.toUpperCase() === rule.equals.toUpperCase()) {
      return rule.classify;
    }
    if (rule.contains !== undefined && value.toUpperCase().includes(rule.contains.toUpperCase())) {
      return rule.classify;
    }
  }
  return direction === 'credit' ? 'donor_payment' : 'other';
}

/**
 * What makes a line the same line across two uploads of overlapping periods.
 *
 * A bank reference identifies a line together with its booked date, signed amount
 * and currency. Otherwise the key is
 * the row's content **plus its occurrence index within the file**, because two
 * genuinely identical transfers on one day are two lines and a plain content
 * hash would merge them — while the legacy, which deduped only rows carrying a
 * reference, re-inserted every referenceless row on every upload.
 */
export function dedupeKey(line: ParsedStatementLine, occurrence: number): string {
  if (line.externalRef !== null && line.externalRef.trim() !== '') {
    return `ref:${line.bookedOn}|${line.direction}|${line.amount.currency}|${line.amount.amountMinor}|${line.externalRef.trim()}`;
  }
  const content = [
    line.bookedOn,
    line.valueDate ?? '',
    line.direction,
    line.amount.amountMinor.toString(),
    line.amount.currency,
    normalizeDescription(line.description),
    line.balanceAfterMinor === null ? '' : line.balanceAfterMinor.toString(),
  ].join('|');
  return `hash:${sha256Hex(content)}#${occurrence}`;
}

function normalizeDescription(description: string | null): string {
  if (description === null) return '';
  return lowerTurkish(description).replace(/\s+/g, ' ').trim();
}

/** The occurrence index of each line among the identical-content rows before it. */
export function dedupeKeys(lines: readonly ParsedStatementLine[]): string[] {
  const seen = new Map<string, number>();
  return lines.map((line) => {
    const provisional = dedupeKey(line, 0);
    if (provisional.startsWith('ref:')) return provisional;
    const base = provisional.slice(0, provisional.lastIndexOf('#'));
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return `${base}#${occurrence}`;
  });
}

export type BalanceGap = {
  readonly index: number;
  readonly expectedMinor: bigint;
  readonly actualMinor: bigint;
};

export function signedMinor(line: ParsedStatementLine): bigint {
  return line.direction === 'credit' ? line.amount.amountMinor : -line.amount.amountMinor;
}

/**
 * Where a statement's running balance disagrees with its own lines — a page
 * that was never exported, or a row the mapping read wrong. Only consecutive
 * lines that both carry a balance are compared, and a currency change breaks
 * the chain rather than producing a false gap.
 */
export function balanceGaps(lines: readonly ParsedStatementLine[]): BalanceGap[] {
  const gaps: BalanceGap[] = [];
  for (let index = 1; index < lines.length; index += 1) {
    const previous = lines[index - 1] as ParsedStatementLine;
    const current = lines[index] as ParsedStatementLine;
    if (previous.balanceAfterMinor === null || current.balanceAfterMinor === null) continue;
    if (previous.amount.currency !== current.amount.currency) continue;
    const expectedMinor = previous.balanceAfterMinor + signedMinor(current);
    if (expectedMinor !== current.balanceAfterMinor) {
      gaps.push({ index, expectedMinor, actualMinor: current.balanceAfterMinor });
    }
  }
  return gaps;
}

/**
 * One row of a statement file as the preview keeps it: its own number in the
 * file, what it said, what it was read as, and why it could not be used.
 */
export type StatementFileRow = {
  readonly rowNumber: number;
  readonly rawRow: Readonly<Record<string, unknown>>;
  readonly line: ParsedStatementLine | null;
  readonly issues: readonly StatementRowIssue[];
};

/**
 * A whole file read. `accountIdentifiers` are the account numbers the file
 * itself names — a CAMT.053 IBAN, an MT940 `:25:` — so a statement uploaded
 * against the wrong account is refused rather than committed to it.
 */
export type StatementRead = {
  readonly accountIdentifiers: readonly string[];
  readonly rows: readonly StatementFileRow[];
};
